/**
 * Phase 23 (Plan 23-02) — Shared widget state primitives.
 *
 * Every dashboard widget composes the same Card-Header-Body shell with a
 * 4-state branch: skeleton → empty → error → populated.
 */

import type { JSX, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/lib/api/error';

export interface WidgetCardProps {
  readonly title: string;
  readonly testId: string;
  readonly children: ReactNode;
  readonly className?: string;
  readonly headerExtra?: ReactNode;
}

/** Common card shell used by every widget — Card + Header + Title + Content. */
export function WidgetCard({
  title,
  testId,
  children,
  className,
  headerExtra,
}: WidgetCardProps): JSX.Element {
  return (
    <Card data-testid={testId} className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {headerExtra}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export interface WidgetSkeletonProps {
  readonly rows?: number;
  readonly testId: string;
}

/** Loading-state primitive — N skeleton bars. */
export function WidgetSkeleton({ rows = 3, testId }: WidgetSkeletonProps): JSX.Element {
  return (
    <div className="space-y-3" data-testid={testId}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

export interface WidgetEmptyProps {
  readonly message: string;
  readonly testId: string;
}

/** Empty-state primitive — centered muted message. */
export function WidgetEmpty({ message, testId }: WidgetEmptyProps): JSX.Element {
  return (
    <div
      className="text-muted-foreground py-8 text-center text-sm"
      data-testid={testId}
    >
      {message}
    </div>
  );
}

export interface WidgetErrorProps {
  readonly error: ApiError;
  readonly onRetry: () => void;
  readonly testId: string;
}

/** Error-state primitive — destructive-tinted box + Retry button. */
export function WidgetError({ error, onRetry, testId }: WidgetErrorProps): JSX.Element {
  return (
    <div
      className="border-destructive/50 bg-destructive/10 text-destructive rounded-md border p-3 text-sm"
      data-testid={testId}
    >
      <strong>Error {String(error.status)}:</strong> {error.message}
      <Button
        variant="outline"
        size="sm"
        className="ml-3"
        onClick={onRetry}
        data-testid={`${testId}-retry-btn`}
      >
        Retry
      </Button>
    </div>
  );
}
